import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faMapMarkerAlt, faCalculator, faRedo, faInfoCircle, faLightbulb, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { calculateLocationDeliveryFee, getDeliveryFeeExplanation, determineServingOffice, OFFICE_LOCATIONS } from '../../services/distanceService';

const LocationForm = ({ location, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    city: '',
    state: 'IN',
    zipCodes: '',
    deliveryFee: 0,
    assignedOffice: '', // 'angola' or 'elkhart'
    isActive: true,
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Mileage calculation state
  const [feeCalculation, setFeeCalculation] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isManuallyOverridden, setIsManuallyOverridden] = useState(false);
  const [calculationError, setCalculationError] = useState('');
  
  // Office assignment state
  const [officeAssignment, setOfficeAssignment] = useState(null);
  const [isDetectingOffice, setIsDetectingOffice] = useState(false);
  const [officeDetectionError, setOfficeDetectionError] = useState('');
  
  // Recommendations state
  const [recommendations, setRecommendations] = useState(null);
  const [showRecommendations, setShowRecommendations] = useState(true);

  // Populate form when editing
  useEffect(() => {
    if (isEditing && location) {
      setFormData({
        city: location.city || '',
        state: location.state || 'IN',
        zipCodes: location.zipCodes ? location.zipCodes.join(', ') : '',
        deliveryFee: location.deliveryFee || 0,
        assignedOffice: location.assignedOffice || '',
        isActive: location.isActive ?? true,
        notes: location.notes || ''
      });
      
      // Check if this was manually overridden (has calculation metadata)
      if (location.calculation) {
        setFeeCalculation(location.calculation);
        setIsManuallyOverridden(location.deliveryFee !== location.calculation.fee);
      }
    }
  }, [isEditing, location]);

  // Auto-detect office and calculate delivery fee when location data changes
  useEffect(() => {
    if (formData.city && formData.state && !isManuallyOverridden) {
      // Get the first ZIP code for calculation
      const firstZip = formData.zipCodes.split(',')[0]?.trim();
      if (firstZip && /^\d{5}$/.test(firstZip)) {
        // Run office detection and fee calculation in parallel
        Promise.all([
          detectOfficeAssignment(formData.city, formData.state, firstZip),
          calculateDeliveryFeeAuto(formData.city, formData.state, firstZip)
        ]).catch(error => {
          console.error('Error in auto-calculations:', error);
        });
      }
    }
  }, [formData.city, formData.state, formData.zipCodes, isManuallyOverridden]);

  // Generate recommendations when office assignment or fee calculation data changes
  useEffect(() => {
    if (officeAssignment && feeCalculation) {
      const recs = generateRecommendations(officeAssignment, feeCalculation);
      setRecommendations(recs);
      setShowRecommendations(true);
    }
  }, [officeAssignment, feeCalculation]);

  // Trigger calculations and recommendations for existing locations when editing starts
  useEffect(() => {
    if (isEditing && location && formData.city && formData.state && formData.zipCodes) {
      const firstZip = formData.zipCodes.split(',')[0]?.trim();
      if (firstZip && /^\d{5}$/.test(firstZip)) {
        // Give a small delay to ensure form is fully loaded
        setTimeout(() => {
          Promise.all([
            detectOfficeAssignment(formData.city, formData.state, firstZip),
            calculateDeliveryFeeAuto(formData.city, formData.state, firstZip)
          ]).catch(error => {
            console.error('Error in edit mode calculations:', error);
          });
        }, 500);
      }
    }
  }, [isEditing, location?.id]); // Only trigger when starting to edit a specific location

  const validateForm = () => {
    const newErrors = {};

    if (!formData.city.trim()) {
      newErrors.city = 'City name is required';
    }

    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }

    if (formData.deliveryFee < 0) {
      newErrors.deliveryFee = 'Delivery fee cannot be negative';
    }
    
    if (!formData.assignedOffice) {
      newErrors.assignedOffice = 'Please assign this location to an office';
    }

    // Validate ZIP codes if provided
    if (formData.zipCodes.trim()) {
      const zipArray = formData.zipCodes.split(',').map(zip => zip.trim());
      const invalidZips = zipArray.filter(zip => !/^\d{5}(-\d{4})?$/.test(zip));
      
      if (invalidZips.length > 0) {
        newErrors.zipCodes = 'Please enter valid ZIP codes (e.g., 12345 or 12345-6789)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const locationData = {
        city: formData.city.trim(),
        state: formData.state.trim(),
        zipCodes: formData.zipCodes.trim() 
          ? formData.zipCodes.split(',').map(zip => zip.trim()).filter(zip => zip)
          : [],
        deliveryFee: parseFloat(formData.deliveryFee) || 0,
        assignedOffice: formData.assignedOffice,
        isActive: formData.isActive,
        notes: formData.notes.trim(),
        // Store calculation metadata for future reference
        calculation: feeCalculation,
        isManuallyOverridden: isManuallyOverridden,
        calculatedAt: feeCalculation ? new Date().toISOString() : null
      };

      await onSave(locationData);
      
      // Reset form if not editing
      if (!isEditing) {
        setFormData({
          city: '',
          state: 'IN',
          zipCodes: '',
          deliveryFee: 0,
          assignedOffice: '',
          isActive: true,
          notes: ''
        });
      }
    } catch (error) {
      console.error('Error saving location:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-detect office assignment
  const detectOfficeAssignment = async (city, state, zipCode) => {
    try {
      setIsDetectingOffice(true);
      setOfficeDetectionError('');
      
      const officeData = await determineServingOffice(city, state, zipCode);
      setOfficeAssignment(officeData);
      
      // Auto-suggest the office if not already set
      if (!formData.assignedOffice) {
        setFormData(prev => ({ ...prev, assignedOffice: officeData.suggestedOffice }));
      }
      
      // Generate recommendations if we also have fee calculation
      if (feeCalculation && officeData) {
        const recs = generateRecommendations(officeData, feeCalculation);
        setRecommendations(recs);
      }
      
      return officeData;
    } catch (error) {
      console.error('Error detecting office assignment:', error);
      setOfficeDetectionError(error.message);
      return null;
    } finally {
      setIsDetectingOffice(false);
    }
  };

  // Generate intelligent recommendations based on location data
  const generateRecommendations = (officeData, feeCalc) => {
    if (!officeData || !feeCalc) return null;

    const recommendations = {
      office: {
        recommended: officeData.suggestedOffice,
        reason: `Closest office at ${officeData.closestDistance} miles`,
        status: officeData.withinRange ? 'good' : 'warning',
        message: officeData.withinRange 
          ? `✅ This location is within your 30-mile delivery range from ${OFFICE_LOCATIONS[officeData.suggestedOffice].name}`
          : `⚠️ This location is ${officeData.closestDistance} miles away, which exceeds your 30-mile delivery limit`
      },
      deliveryFee: {
        recommended: feeCalc.fee,
        calculation: `${Math.ceil(feeCalc.distance)} miles × $2/mile = $${Math.ceil(feeCalc.distance) * 2}`,
        finalCalculation: feeCalc.calculations.isMinimumApplied 
          ? `Applied minimum fee of $${feeCalc.fee}` 
          : `Final fee: $${feeCalc.fee}`,
        status: 'good',
        message: `💰 Based on $2 per mile calculation, the delivery fee should be $${feeCalc.fee}`
      },
      status: {
        recommended: true,
        reason: officeData.withinRange ? 'Location is within delivery range' : 'Location exceeds delivery range',
        status: officeData.withinRange ? 'good' : 'warning',
        message: officeData.withinRange 
          ? '🎯 Recommended to activate this location'
          : '❌ Not recommended - outside delivery range'
      },
      summary: {
        canDeliver: officeData.withinRange,
        primaryOffice: OFFICE_LOCATIONS[officeData.suggestedOffice].name,
        distance: officeData.closestDistance,
        estimatedFee: feeCalc.fee,
        driveTime: Math.round(officeData.closestDistance * 2) + ' minutes' // Rough estimate: 1 mile = 2 minutes
      }
    };

    return recommendations;
  };

  // Auto-calculate delivery fee based on distance
  const calculateDeliveryFeeAuto = async (city, state, zipCode) => {
    try {
      setIsCalculating(true);
      setCalculationError('');
      
      const calculation = await calculateLocationDeliveryFee(city, state, zipCode, formData.assignedOffice || null);
      setFeeCalculation(calculation);
      
      // Generate recommendations if we have office assignment data
      if (officeAssignment && calculation) {
        const recs = generateRecommendations(officeAssignment, calculation);
        setRecommendations(recs);
      }
      
      // Only set the fee if not manually overridden
      if (!isManuallyOverridden) {
        setFormData(prev => ({ ...prev, deliveryFee: calculation.fee }));
      }
    } catch (error) {
      console.error('Error calculating delivery fee:', error);
      setCalculationError(error.message);
    } finally {
      setIsCalculating(false);
    }
  };

  // Manually trigger calculation and office detection
  const handleCalculateDeliveryFee = async () => {
    const firstZip = formData.zipCodes.split(',')[0]?.trim();
    if (!formData.city || !formData.state || !firstZip) {
      setCalculationError('Please enter city, state, and at least one ZIP code');
      return;
    }
    
    if (!/^\d{5}$/.test(firstZip)) {
      setCalculationError('Please enter a valid 5-digit ZIP code');
      return;
    }
    
    // Run both office detection and fee calculation
    try {
      await Promise.all([
        detectOfficeAssignment(formData.city, formData.state, firstZip),
        calculateDeliveryFeeAuto(formData.city, formData.state, firstZip)
      ]);
      setIsManuallyOverridden(false); // Reset override when manually calculating
    } catch (error) {
      console.error('Error in manual calculations:', error);
    }
  };

  // Apply all recommended settings
  const applyRecommendedSettings = () => {
    if (!recommendations) return;

    setFormData(prev => ({
      ...prev,
      assignedOffice: recommendations.office.recommended,
      deliveryFee: recommendations.deliveryFee.recommended,
      isActive: recommendations.summary.canDeliver
    }));

    setIsManuallyOverridden(false);
    setShowRecommendations(false);
  };

  // Apply individual recommendation
  const applyIndividualRecommendation = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'deliveryFee') {
      setIsManuallyOverridden(false);
    }
  };

  // Apply calculated fee
  const handleApplyCalculatedFee = () => {
    if (feeCalculation) {
      setFormData(prev => ({ ...prev, deliveryFee: feeCalculation.fee }));
      setIsManuallyOverridden(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Check if delivery fee is being manually changed
    if (name === 'deliveryFee' && feeCalculation && parseFloat(value) !== feeCalculation.fee) {
      setIsManuallyOverridden(true);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="form-modal">
      <div className="form-modal-content">
        <div className="form-header">
          <h3>
            <FontAwesomeIcon icon={faMapMarkerAlt} />
            {isEditing ? 'Edit Location' : 'Add New Location'}
          </h3>
          <button type="button" onClick={onCancel} className="close-btn">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Smart Recommendations Panel */}
        {recommendations && showRecommendations && (
          <div className="recommendations-panel">
            <div className="recommendations-header">
              <h4>
                <FontAwesomeIcon icon={faLightbulb} />
                Smart Recommendations
              </h4>
              <button 
                type="button" 
                onClick={() => setShowRecommendations(false)}
                className="hide-recommendations"
                title="Hide recommendations"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className="recommendations-summary">
              <div className={`summary-card ${recommendations.summary.canDeliver ? 'deliverable' : 'not-deliverable'}`}>
                <div className="summary-header">
                  <FontAwesomeIcon icon={recommendations.summary.canDeliver ? faCheckCircle : faExclamationTriangle} />
                  <span className="summary-title">
                    {recommendations.summary.canDeliver ? 'Deliverable Location' : 'Outside Delivery Range'}
                  </span>
                </div>
                <div className="summary-details">
                  <p><strong>{recommendations.summary.distance} miles</strong> from {recommendations.summary.primaryOffice}</p>
                  <p>Estimated drive time: <strong>{recommendations.summary.driveTime}</strong></p>
                  <p>Recommended fee: <strong>${recommendations.summary.estimatedFee}</strong></p>
                </div>
              </div>
            </div>

            <div className="recommendations-grid">
              <div className={`recommendation-card office-rec ${recommendations.office.status}`}>
                <div className="rec-header">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span>Office Assignment</span>
                </div>
                <p className="rec-message">{recommendations.office.message}</p>
                <div className="rec-actions">
                  <button
                    type="button"
                    onClick={() => applyIndividualRecommendation('assignedOffice', recommendations.office.recommended)}
                    className="apply-rec-btn"
                    disabled={formData.assignedOffice === recommendations.office.recommended}
                  >
                    {formData.assignedOffice === recommendations.office.recommended ? 'Applied' : 'Apply'}
                  </button>
                </div>
              </div>

              <div className={`recommendation-card fee-rec ${recommendations.deliveryFee.status}`}>
                <div className="rec-header">
                  <FontAwesomeIcon icon={faCalculator} />
                  <span>Delivery Fee</span>
                </div>
                <p className="rec-message">{recommendations.deliveryFee.message}</p>
                <p className="rec-calculation">{recommendations.deliveryFee.calculation}</p>
                <p className="rec-final">{recommendations.deliveryFee.finalCalculation}</p>
                <div className="rec-actions">
                  <button
                    type="button"
                    onClick={() => applyIndividualRecommendation('deliveryFee', recommendations.deliveryFee.recommended)}
                    className="apply-rec-btn"
                    disabled={formData.deliveryFee === recommendations.deliveryFee.recommended}
                  >
                    {formData.deliveryFee === recommendations.deliveryFee.recommended ? 'Applied' : 'Apply'}
                  </button>
                </div>
              </div>

              <div className={`recommendation-card status-rec ${recommendations.status.status}`}>
                <div className="rec-header">
                  <FontAwesomeIcon icon={recommendations.status.recommended ? faCheckCircle : faExclamationTriangle} />
                  <span>Location Status</span>
                </div>
                <p className="rec-message">{recommendations.status.message}</p>
                <div className="rec-actions">
                  <button
                    type="button"
                    onClick={() => applyIndividualRecommendation('isActive', recommendations.status.recommended)}
                    className="apply-rec-btn"
                    disabled={formData.isActive === recommendations.status.recommended}
                  >
                    {formData.isActive === recommendations.status.recommended ? 'Applied' : 'Apply'}
                  </button>
                </div>
              </div>
            </div>

            <div className="recommendations-actions">
              <button
                type="button"
                onClick={applyRecommendedSettings}
                className="apply-all-btn"
                disabled={!recommendations.summary.canDeliver}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Apply All Recommendations
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="coupon-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="city">City Name *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className={errors.city ? 'error' : ''}
                placeholder="Enter city name"
                required
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="state">State *</label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                className={errors.state ? 'error' : ''}
                required
              >
                <option value="IN">Indiana</option>
                <option value="OH">Ohio</option>
                <option value="MI">Michigan</option>
                <option value="KY">Kentucky</option>
                <option value="IL">Illinois</option>
              </select>
              {errors.state && <span className="error-message">{errors.state}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="zipCodes">ZIP Codes (Optional)</label>
              <input
                type="text"
                id="zipCodes"
                name="zipCodes"
                value={formData.zipCodes}
                onChange={handleInputChange}
                className={errors.zipCodes ? 'error' : ''}
                placeholder="e.g., 46703, 46804, 46805"
              />
              <small className="form-help">Separate multiple ZIP codes with commas</small>
              {errors.zipCodes && <span className="error-message">{errors.zipCodes}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="assignedOffice">
                Assigned Office *
                {isDetectingOffice && (
                  <span className="detecting-office">
                    <FontAwesomeIcon icon={faRedo} spin />
                    Detecting...
                  </span>
                )}
              </label>
              
              <select
                id="assignedOffice"
                name="assignedOffice"
                value={formData.assignedOffice}
                onChange={handleInputChange}
                className={errors.assignedOffice ? 'error' : ''}
                disabled={isDetectingOffice}
              >
                <option value="">Select an office</option>
                <option value="angola">Angola Office</option>
                <option value="elkhart">Elkhart Office</option>
              </select>
              
              {officeAssignment && (
                <div className="office-assignment-info">
                  <small className="office-distances">
                    Distance to Angola: {officeAssignment.distances.angola} miles | 
                    Distance to Elkhart: {officeAssignment.distances.elkhart} miles
                  </small>
                  {officeAssignment.suggestedOffice && (
                    <small className="office-suggestion">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      Suggested: {OFFICE_LOCATIONS[officeAssignment.suggestedOffice].name}
                      {!officeAssignment.withinRange && (
                        <span className="range-warning"> ⚠️ Outside 30-mile range</span>
                      )}
                    </small>
                  )}
                </div>
              )}
              
              {officeDetectionError && (
                <span className="error-message">{officeDetectionError}</span>
              )}
              {errors.assignedOffice && <span className="error-message">{errors.assignedOffice}</span>}
              
              {/* Show recommendations button if they exist but are hidden */}
              {recommendations && !showRecommendations && (
                <div className="show-recommendations-container">
                  <button
                    type="button"
                    onClick={() => setShowRecommendations(true)}
                    className="show-recommendations-btn"
                  >
                    <FontAwesomeIcon icon={faLightbulb} />
                    Show Smart Recommendations
                  </button>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="deliveryFee">
                Delivery Fee ($)
                {feeCalculation && (
                  <span className="fee-calculation-info">
                    <FontAwesomeIcon icon={faInfoCircle} />
                    Auto-calculated based on mileage
                  </span>
                )}
              </label>
              
              <div className="delivery-fee-container">
                <input
                  type="number"
                  id="deliveryFee"
                  name="deliveryFee"
                  value={formData.deliveryFee}
                  onChange={handleInputChange}
                  className={`${errors.deliveryFee ? 'error' : ''} ${isManuallyOverridden ? 'manually-overridden' : ''}`}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
                
                <div className="delivery-fee-actions">
                  <button
                    type="button"
                    onClick={handleCalculateDeliveryFee}
                    disabled={isCalculating || !formData.city || !formData.state}
                    className="calculate-btn"
                    title="Calculate delivery fee based on distance"
                  >
                    <FontAwesomeIcon icon={isCalculating ? faRedo : faCalculator} spin={isCalculating} />
                    {isCalculating ? 'Calculating...' : 'Calculate'}
                  </button>
                  
                  {feeCalculation && isManuallyOverridden && (
                    <button
                      type="button"
                      onClick={handleApplyCalculatedFee}
                      className="apply-calculated-btn"
                      title={`Apply calculated fee: $${feeCalculation.fee}`}
                    >
                      Apply ${feeCalculation.fee}
                    </button>
                  )}
                </div>
              </div>
              
              {/* Calculation Results */}
              {feeCalculation && (
                <div className={`calculation-result ${isManuallyOverridden ? 'overridden' : 'applied'}`}>
                  <div className="calculation-summary">
                    <strong>📍 {getDeliveryFeeExplanation(feeCalculation)}</strong>
                  </div>
                  {isManuallyOverridden && (
                    <div className="override-notice">
                      ⚠️ Manual override: ${formData.deliveryFee} (calculated: ${feeCalculation.fee})
                    </div>
                  )}
                </div>
              )}
              
              {calculationError && (
                <div className="calculation-error">
                  ❌ {calculationError}
                </div>
              )}
              
              {errors.deliveryFee && <span className="error-message">{errors.deliveryFee}</span>}
            </div>
          </div>

          <div className="form-group full-width">
            <label htmlFor="notes">Notes (Optional)</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Additional notes about this location (e.g., special instructions, restrictions)"
              rows="3"
            />
          </div>

          <div className="form-group full-width checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
              />
              <span className="checkbox-text">Active (available for customer selection)</span>
            </label>
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faTimes} />
              Cancel
            </button>
            
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faSave} />
              {isSubmitting ? 'Saving...' : (isEditing ? 'Update Location' : 'Add Location')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LocationForm; 